// src/pages/SignUp.tsx

import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import { sendNewClientSignupNotification } from '../lib/email/emailService';
import { logAuthenticationEvent } from '../lib/audit/auditLogger';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Validate date of birth
    if (!dateOfBirth) {
      setError('Date of birth is required');
      return;
    }

    // Calculate age
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // Check age restrictions
    if (age < 13) {
      setError('You must be at least 13 years old to use this service. If you are under 13, please have a parent or guardian contact us.');
      return;
    }

    if (age >= 13 && age < 16) {
      if (!parentalConsent) {
        setError('Users between 13 and 16 years of age require parental consent. Please check the parental consent box.');
        return;
      }
    }

    if (age < 18) {
      setIsMinor(true);
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user document in Firestore with default 'client' role
      // displayName will be set when user completes onboarding form
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email || email,
        displayName: '', // Will be set from fullName during onboarding
        profilePictureUrl: '',
        role: 'client', // Default role for new users
        dateOfBirth: dateOfBirth,
        isMinor: isMinor,
        parentalConsentProvided: age >= 13 && age < 16 ? parentalConsent : null,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Send notification email (don't wait for it to complete)
      sendNewClientSignupNotification(user.email || email, user.uid).catch((err) => {
        console.error('Failed to send signup notification email:', err);
      });
      
      // Log successful signup
      logAuthenticationEvent(
        user.uid,
        user.email || email,
        'client',
        'user_signup',
        true
      ).catch((err) => console.error('Failed to log signup event:', err));
      
      navigate('/onboarding'); // Redirect to onboarding form after sign-up
    } catch (err: any) {
      setError(err.message);
      
      // Log failed signup attempt
      logAuthenticationEvent(
        '',
        email,
        'client',
        'user_signup',
        false,
        err.message
      ).catch((logErr) => console.error('Failed to log failed signup:', logErr));
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: '2rem auto', padding: '20px' }}>
      <h2>Create Account</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Date of Birth <span style={{ color: 'red' }}>*</span></label>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(e) => {
              setDateOfBirth(e.target.value);
              // Calculate age when date changes
              if (e.target.value) {
                const birthDate = new Date(e.target.value);
                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                  age--;
                }
                setIsMinor(age < 18);
              }
            }}
            required
            max={new Date().toISOString().split('T')[0]} // Cannot be in the future
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
          <small style={{ color: '#666', fontSize: '0.875rem' }}>
            Required for age verification. Must be 13 or older to use this service.
          </small>
        </div>

        {isMinor && (
          <div style={{
            marginBottom: '15px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={parentalConsent}
                onChange={(e) => setParentalConsent(e.target.checked)}
                style={{ marginRight: '8px', marginTop: '2px' }}
              />
              <span style={{ fontSize: '0.875rem' }}>
                <strong>Parental Consent Required:</strong> I confirm that I am between 13 and 16 years old 
                and have obtained consent from my parent or guardian to use this service. By checking this box, 
                I represent that my parent or guardian has reviewed and agreed to the{' '}
                <Link to="/privacy-policy" style={{ color: '#007bff', textDecoration: 'none' }}>
                  Privacy Policy
                </Link>.
              </span>
            </label>
          </div>
        )}

        {/* Notice at Collection */}
        <div style={{
          marginBottom: '15px',
          padding: '12px',
          backgroundColor: '#e7f3ff',
          border: '1px solid #007bff',
          borderRadius: '4px',
          fontSize: '0.875rem'
        }}>
          <strong>Notice at Collection (CCPA/CPRA):</strong>
          <p style={{ margin: '8px 0 0 0', lineHeight: '1.5' }}>
            By creating an account, you agree that we may collect and use the following categories of 
            personal information: <strong>Identifiers</strong> (name, email, phone, address), 
            <strong> Financial Information</strong> (payment details, billing information), 
            <strong> Commercial Information</strong> (transaction history, services used), and 
            <strong> Internet Activity</strong> (usage data, device information). We collect this information 
            to provide our services, process payments, communicate with you, and comply with legal obligations. 
            For more details, see our{' '}
            <Link to="/privacy-policy" style={{ color: '#007bff', textDecoration: 'none' }}>
              Privacy Policy
            </Link>.
          </p>
        </div>

        {error && <p style={{ color: 'red' }}>{error}</p>}

        <button 
          type="submit"
          style={{ 
            backgroundColor: '#28a745', 
            color: 'white', 
            padding: '10px 20px', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Sign Up
        </button>
      </form>
      
      <p style={{ textAlign: 'center', marginTop: '20px' }}>
        Already have an account? <Link to="/sign-in">Sign In</Link>
      </p>
      
      <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.875rem', color: '#666' }}>
        By creating an account, you agree to our{' '}
        <Link to="/privacy-policy" style={{ color: '#007bff', textDecoration: 'none' }}>
          Privacy Policy
        </Link>
      </p>
    </div>
  );
};

export default SignUp;
